import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import myaxios from "@/lib/axios";

/**
 * Where the API sends the browser once the identity provider has authenticated
 * the user.
 *
 * The URL carries a single-use code rather than the session token itself, so
 * nothing long-lived ends up in browser history or a Referer header. This page
 * trades that code for the real token over POST — the same exchange the password
 * login performs — and then leaves.
 */
function SSOCallback() {
  const [error, setError] = useState<string>();

  // The code may be redeemed exactly once. React's StrictMode runs effects twice
  // in development, and the second run would spend an already-burnt code and
  // report a failure for a sign-in that actually succeeded. A ref latches
  // synchronously; a state flag would not have been applied in time.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const ssoCode = params.get("sso_code");
    const next = params.get("next") || "/";

    if (!ssoCode) {
      setError("This sign-in link is incomplete. Please try signing in again.");
      return;
    }

    myaxios
      .post("/api/auth/sso/exchange", { sso_code: ssoCode })
      .then((response) => {
        if (!response.data?.success || !response.data?.token) {
          setError(
            response.data?.message ??
              "This sign-in link has expired. Please try signing in again."
          );
          return;
        }

        // Written here rather than through a shared helper so this feature adds
        // nothing to the existing password-login path. Attributes match the ones
        // LoginPage already sets.
        document.cookie = `auth_token=${response.data.token}; path=/; max-age=86400; secure; sameSite=strict`;

        // A full page load, not a client-side route change: the API client seeds
        // its token once at module import, so an in-app navigation would leave it
        // without one.
        window.location.href = next;
      })
      .catch(() => {
        setError("We could not complete your sign-in. Please try again.");
      });
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-900 px-4">
      {error ? (
        <div className="max-w-md rounded-lg border border-gray-700 bg-gray-800 p-6 text-center">
          <h1 className="mb-2 text-lg font-semibold text-white">
            Sign-in failed
          </h1>
          <p className="mb-4 text-sm text-gray-300">{error}</p>
          <Link to="/login" className="text-blue-400 hover:text-blue-300">
            Back to sign in
          </Link>
        </div>
      ) : (
        <div className="text-center text-gray-300">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-blue-400" />
          <p>Signing you in…</p>
        </div>
      )}
    </div>
  );
}

export default SSOCallback;
